// Copyright 2026, Auxilio Technologies LLC

// Shared sites data used across the application (Sites, Control Tower, etc.)

export interface Site {
  id: string;
  name: string;
  code: string;
  type: 'warehouse' | 'distribution_center' | 'cross_dock' | 'fulfillment_center' | 'cold_storage';
  status: 'operational' | 'maintenance' | 'limited' | 'offline';
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  coordinates: {
    lat: number;
    lng: number;
  };
  contact: {
    manager: string;
    phone: string;
    email: string;
  };
  capacity: {
    total: number;
    used: number;
    unit: string;
  };
  employees: number;
  docks: {
    inbound: number;
    outbound: number;
  };
  shipmentsToday: {
    inbound: number;
    outbound: number;
  };
  avgProcessingTime: number; // hours
  region: string;
  certifications: string[];
  operatingHours: string;
  lastInspection: string;
}

// US city coordinates lookup for generating coordinates when adding new sites
export const cityCoordinates: Record<string, { lat: number; lng: number }> = {
  // Major US cities - covers most common logistics hub locations
  'chicago': { lat: 41.8781, lng: -87.6298 },
  'atlanta': { lat: 33.749, lng: -84.388 },
  'los angeles': { lat: 33.7866, lng: -118.2987 },
  'newark': { lat: 40.6895, lng: -74.1745 },
  'dallas': { lat: 32.7767, lng: -96.797 },
  'seattle': { lat: 47.5343, lng: -122.2967 },
  'miami': { lat: 25.8006, lng: -80.2873 },
  'phoenix': { lat: 33.4255, lng: -112.1527 },
  'denver': { lat: 39.7392, lng: -104.9903 },
  'houston': { lat: 29.7604, lng: -95.3698 },
  'new york': { lat: 40.7128, lng: -74.006 },
  'san francisco': { lat: 37.7749, lng: -122.4194 },
  'boston': { lat: 42.3601, lng: -71.0589 },
  'philadelphia': { lat: 39.9526, lng: -75.1652 },
  'detroit': { lat: 42.3314, lng: -83.0458 },
  'minneapolis': { lat: 44.9778, lng: -93.265 },
  'portland': { lat: 45.5155, lng: -122.6789 },
  'salt lake city': { lat: 40.7608, lng: -111.891 },
  'kansas city': { lat: 39.0997, lng: -94.5786 },
  'memphis': { lat: 35.1495, lng: -90.049 },
  'nashville': { lat: 36.1627, lng: -86.7816 },
  'charlotte': { lat: 35.2271, lng: -80.8431 },
  'indianapolis': { lat: 39.7684, lng: -86.1581 },
  'columbus': { lat: 39.9612, lng: -82.9988 },
  'las vegas': { lat: 36.1699, lng: -115.1398 },
  'san diego': { lat: 32.7157, lng: -117.1611 },
  'san antonio': { lat: 29.4241, lng: -98.4936 },
  'austin': { lat: 30.2672, lng: -97.7431 },
  'jacksonville': { lat: 30.3322, lng: -81.6557 },
  'baltimore': { lat: 39.2904, lng: -76.6122 },
  'milwaukee': { lat: 43.0389, lng: -87.9065 },
  'louisville': { lat: 38.2527, lng: -85.7585 },
  'oklahoma city': { lat: 35.4676, lng: -97.5164 },
  'tucson': { lat: 32.2226, lng: -110.9747 },
  'cincinnati': { lat: 39.1031, lng: -84.512 },
  'cleveland': { lat: 41.4993, lng: -81.6944 },
  'pittsburgh': { lat: 40.4406, lng: -79.9959 },
  'new orleans': { lat: 29.9511, lng: -90.0715 },
  'st louis': { lat: 38.627, lng: -90.1994 },
  'tampa': { lat: 27.9506, lng: -82.4572 },
  'orlando': { lat: 28.5383, lng: -81.3792 },
  'raleigh': { lat: 35.7796, lng: -78.6382 },
};

// Helper function to get coordinates for a city, with fallback to approximate US center
export const getCityCoordinates = (city: string, state: string): { lat: number; lng: number } => {
  const cityLower = city.toLowerCase().trim();

  // Check direct city match
  if (cityCoordinates[cityLower]) {
    return cityCoordinates[cityLower];
  }

  // State-based approximate coordinates as fallback
  const stateCoordinates: Record<string, { lat: number; lng: number }> = {
    'AL': { lat: 32.806671, lng: -86.79113 },
    'AK': { lat: 61.370716, lng: -152.404419 },
    'AZ': { lat: 33.729759, lng: -111.431221 },
    'AR': { lat: 34.969704, lng: -92.373123 },
    'CA': { lat: 36.116203, lng: -119.681564 },
    'CO': { lat: 39.059811, lng: -105.311104 },
    'CT': { lat: 41.597782, lng: -72.755371 },
    'DE': { lat: 39.318523, lng: -75.507141 },
    'FL': { lat: 27.766279, lng: -81.686783 },
    'GA': { lat: 33.040619, lng: -83.643074 },
    'HI': { lat: 21.094318, lng: -157.498337 },
    'ID': { lat: 44.240459, lng: -114.478828 },
    'IL': { lat: 40.349457, lng: -88.986137 },
    'IN': { lat: 39.849426, lng: -86.258278 },
    'IA': { lat: 42.011539, lng: -93.210526 },
    'KS': { lat: 38.5266, lng: -96.726486 },
    'KY': { lat: 37.66814, lng: -84.670067 },
    'LA': { lat: 31.169546, lng: -91.867805 },
    'ME': { lat: 44.693947, lng: -69.381927 },
    'MD': { lat: 39.063946, lng: -76.802101 },
    'MA': { lat: 42.230171, lng: -71.530106 },
    'MI': { lat: 43.326618, lng: -84.536095 },
    'MN': { lat: 45.694454, lng: -93.900192 },
    'MS': { lat: 32.741646, lng: -89.678696 },
    'MO': { lat: 38.456085, lng: -92.288368 },
    'MT': { lat: 46.921925, lng: -110.454353 },
    'NE': { lat: 41.12537, lng: -98.268082 },
    'NV': { lat: 38.313515, lng: -117.055374 },
    'NH': { lat: 43.452492, lng: -71.563896 },
    'NJ': { lat: 40.298904, lng: -74.521011 },
    'NM': { lat: 34.840515, lng: -106.248482 },
    'NY': { lat: 42.165726, lng: -74.948051 },
    'NC': { lat: 35.630066, lng: -79.806419 },
    'ND': { lat: 47.528912, lng: -99.784012 },
    'OH': { lat: 40.388783, lng: -82.764915 },
    'OK': { lat: 35.565342, lng: -96.928917 },
    'OR': { lat: 44.572021, lng: -122.070938 },
    'PA': { lat: 40.590752, lng: -77.209755 },
    'RI': { lat: 41.680893, lng: -71.51178 },
    'SC': { lat: 33.856892, lng: -80.945007 },
    'SD': { lat: 44.299782, lng: -99.438828 },
    'TN': { lat: 35.747845, lng: -86.692345 },
    'TX': { lat: 31.054487, lng: -97.563461 },
    'UT': { lat: 40.150032, lng: -111.862434 },
    'VT': { lat: 44.045876, lng: -72.710686 },
    'VA': { lat: 37.769337, lng: -78.169968 },
    'WA': { lat: 47.400902, lng: -121.490494 },
    'WV': { lat: 38.491226, lng: -80.954453 },
    'WI': { lat: 44.268543, lng: -89.616508 },
    'WY': { lat: 42.755966, lng: -107.30249 },
  };

  const stateUpper = state.toUpperCase().trim();
  if (stateCoordinates[stateUpper]) {
    return stateCoordinates[stateUpper];
  }

  // Default to approximate US center
  return { lat: 39.8283, lng: -98.5795 };
};

export const mockSites: Site[] = [
  {
    id: 'SITE001',
    name: 'Chicago Distribution Hub',
    code: 'CHI-DC-01',
    type: 'distribution_center',
    status: 'operational',
    address: {
      street: '4500 Industrial Pkwy',
      city: 'Chicago',
      state: 'IL',
      zip: '60632',
      country: 'USA',
    },
    coordinates: { lat: 41.8781, lng: -87.6298 },
    contact: {
      manager: 'Robert Martinez',
      phone: '(312) 555-0147',
      email: 'rmartinez@auxilio.com',
    },
    capacity: { total: 250000, used: 187500, unit: 'sq ft' },
    employees: 245,
    docks: { inbound: 24, outbound: 32 },
    shipmentsToday: { inbound: 156, outbound: 203 },
    avgProcessingTime: 4.2,
    region: 'Midwest',
    certifications: ['ISO 9001', 'C-TPAT', 'HAZMAT'],
    operatingHours: '24/7',
    lastInspection: '2025-12-15',
  },
  {
    id: 'SITE002',
    name: 'Atlanta Fulfillment Center',
    code: 'ATL-FC-01',
    type: 'fulfillment_center',
    status: 'operational',
    address: {
      street: '2800 Logistics Way',
      city: 'Atlanta',
      state: 'GA',
      zip: '30349',
      country: 'USA',
    },
    coordinates: { lat: 33.749, lng: -84.388 },
    contact: {
      manager: 'Jennifer Williams',
      phone: '(404) 555-0298',
      email: 'jwilliams@auxilio.com',
    },
    capacity: { total: 180000, used: 162000, unit: 'sq ft' },
    employees: 312,
    docks: { inbound: 18, outbound: 28 },
    shipmentsToday: { inbound: 89, outbound: 445 },
    avgProcessingTime: 2.8,
    region: 'Southeast',
    certifications: ['ISO 9001', 'SOC 2'],
    operatingHours: '24/7',
    lastInspection: '2025-11-28',
  },
  {
    id: 'SITE003',
    name: 'Los Angeles Cross-Dock',
    code: 'LAX-XD-01',
    type: 'cross_dock',
    status: 'operational',
    address: {
      street: '8900 Harbor Blvd',
      city: 'Los Angeles',
      state: 'CA',
      zip: '90744',
      country: 'USA',
    },
    coordinates: { lat: 33.7866, lng: -118.2987 },
    contact: {
      manager: 'Michael Chang',
      phone: '(310) 555-0412',
      email: 'mchang@auxilio.com',
    },
    capacity: { total: 85000, used: 68000, unit: 'sq ft' },
    employees: 98,
    docks: { inbound: 20, outbound: 20 },
    shipmentsToday: { inbound: 178, outbound: 175 },
    avgProcessingTime: 1.5,
    region: 'West Coast',
    certifications: ['C-TPAT', 'FSMA'],
    operatingHours: '5:00 AM - 11:00 PM',
    lastInspection: '2026-01-05',
  },
  {
    id: 'SITE004',
    name: 'Newark Cold Storage',
    code: 'EWR-CS-01',
    type: 'cold_storage',
    status: 'limited',
    address: {
      street: '1200 Port Newark Rd',
      city: 'Newark',
      state: 'NJ',
      zip: '07114',
      country: 'USA',
    },
    coordinates: { lat: 40.6895, lng: -74.1745 },
    contact: {
      manager: 'Sarah Thompson',
      phone: '(973) 555-0633',
      email: 'sthompson@auxilio.com',
    },
    capacity: { total: 120000, used: 114000, unit: 'sq ft' },
    employees: 156,
    docks: { inbound: 12, outbound: 14 },
    shipmentsToday: { inbound: 67, outbound: 82 },
    avgProcessingTime: 3.5,
    region: 'Northeast',
    certifications: ['ISO 9001', 'FSMA', 'SQF'],
    operatingHours: '24/7',
    lastInspection: '2025-12-20',
  },
  {
    id: 'SITE005',
    name: 'Dallas Regional Warehouse',
    code: 'DFW-WH-01',
    type: 'warehouse',
    status: 'operational',
    address: {
      street: '5500 Commerce Dr',
      city: 'Dallas',
      state: 'TX',
      zip: '75212',
      country: 'USA',
    },
    coordinates: { lat: 32.7767, lng: -96.797 },
    contact: {
      manager: 'David Rodriguez',
      phone: '(214) 555-0877',
      email: 'drodriguez@auxilio.com',
    },
    capacity: { total: 320000, used: 224000, unit: 'sq ft' },
    employees: 189,
    docks: { inbound: 16, outbound: 20 },
    shipmentsToday: { inbound: 94, outbound: 127 },
    avgProcessingTime: 5.1,
    region: 'Southwest',
    certifications: ['ISO 9001', 'C-TPAT'],
    operatingHours: '6:00 AM - 10:00 PM',
    lastInspection: '2025-11-15',
  },
  {
    id: 'SITE006',
    name: 'Seattle Distribution Center',
    code: 'SEA-DC-01',
    type: 'distribution_center',
    status: 'maintenance',
    address: {
      street: '3200 Industrial Ave',
      city: 'Seattle',
      state: 'WA',
      zip: '98108',
      country: 'USA',
    },
    coordinates: { lat: 47.5343, lng: -122.2967 },
    contact: {
      manager: 'Emily Parker',
      phone: '(206) 555-0234',
      email: 'eparker@auxilio.com',
    },
    capacity: { total: 175000, used: 122500, unit: 'sq ft' },
    employees: 167,
    docks: { inbound: 14, outbound: 18 },
    shipmentsToday: { inbound: 45, outbound: 68 },
    avgProcessingTime: 4.8,
    region: 'West Coast',
    certifications: ['ISO 9001', 'LEED Gold'],
    operatingHours: '24/7',
    lastInspection: '2025-12-01',
  },
  {
    id: 'SITE007',
    name: 'Miami Import Warehouse',
    code: 'MIA-WH-01',
    type: 'warehouse',
    status: 'operational',
    address: {
      street: '7800 NW 25th St',
      city: 'Miami',
      state: 'FL',
      zip: '33122',
      country: 'USA',
    },
    coordinates: { lat: 25.8006, lng: -80.2873 },
    contact: {
      manager: 'Carlos Mendez',
      phone: '(305) 555-0567',
      email: 'cmendez@auxilio.com',
    },
    capacity: { total: 145000, used: 108750, unit: 'sq ft' },
    employees: 134,
    docks: { inbound: 22, outbound: 16 },
    shipmentsToday: { inbound: 134, outbound: 89 },
    avgProcessingTime: 6.2,
    region: 'Southeast',
    certifications: ['C-TPAT', 'FTZ', 'HAZMAT'],
    operatingHours: '24/7',
    lastInspection: '2025-12-28',
  },
  {
    id: 'SITE008',
    name: 'Phoenix Fulfillment Hub',
    code: 'PHX-FC-01',
    type: 'fulfillment_center',
    status: 'operational',
    address: {
      street: '6100 W Buckeye Rd',
      city: 'Phoenix',
      state: 'AZ',
      zip: '85043',
      country: 'USA',
    },
    coordinates: { lat: 33.4255, lng: -112.1527 },
    contact: {
      manager: 'Amanda Foster',
      phone: '(602) 555-0891',
      email: 'afoster@auxilio.com',
    },
    capacity: { total: 200000, used: 160000, unit: 'sq ft' },
    employees: 278,
    docks: { inbound: 16, outbound: 24 },
    shipmentsToday: { inbound: 112, outbound: 356 },
    avgProcessingTime: 3.1,
    region: 'Southwest',
    certifications: ['ISO 9001', 'SOC 2'],
    operatingHours: '24/7',
    lastInspection: '2026-01-02',
  },
];
